import React, { useState, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete, TextField, FormHelperText, Avatar, Box } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormLabel } from "../../../components-new/utils";
import { useSearchUsers } from "../hooks/useWebLinkQueries";
import { useParams } from "react-router-dom";
import { debounce } from "lodash";
import ThumbnailImage from "../../../components-new/Thumbnail";

const useStyles = makeStyles(() => ({
    root: {
        "& .MuiAutocomplete-inputRoot": {
            borderRadius: "15px",
        },
        "& legend": { display: "none" },
        "& fieldset": { top: 0 },
        "& .MuiInputLabel-root": {
            display: "none",
        },
    },
}));

interface SurveyorAutoCompleteProps {
    name: string;
    label: string;
    placeholder?: string;
}

export const SurveyorAutoComplete: React.FC<SurveyorAutoCompleteProps> = ({
    name,
    label,
    placeholder = "Search and select surveyor",
}) => {
    const classes = useStyles();
    const { projectid } = useParams<{ projectid: string }>();
    const [inputValue, setInputValue] = useState("");
    const [searchQuery, setSearchQuery] = useState("");

    const { data: usersData } = useSearchUsers(projectid, searchQuery);

    const {
        control,
        formState: { errors },
    } = useFormContext();

    // Debounce search query updates
    const debouncedSetSearchQuery = useMemo(
        () =>
            debounce((value: string) => {
                setSearchQuery(value);
            }, 600),
        []
    );

    useEffect(() => {
        debouncedSetSearchQuery(inputValue);
        return () => {
            debouncedSetSearchQuery.cancel();
        };
    }, [inputValue, debouncedSetSearchQuery]);

    const users = useMemo(() => {
        return usersData?.users || [];
    }, [usersData]);

    // Update form value when users load and we have a partial user object (just id)
    const { setValue, watch } = useFormContext();
    const currentValue = watch(name);

    useEffect(() => {
        if (currentValue && typeof currentValue === "object" && currentValue.id && !currentValue.name && users.length > 0) {
            const foundUser = users.find((u: any) => u.id === currentValue.id);
            if (foundUser) {
                setValue(name, foundUser, { shouldDirty: false });
            }
        }
    }, [users, currentValue, name, setValue]);

    // Find user by ID if value is just an object with id
    const findUserById = (value: any) => {
        if (!value) return null;
        if (typeof value === "object" && value.id && !value.name) {
            // Value is just { id: X }, find the full user object
            const foundUser = users.find((u: any) => u.id === value.id);
            return foundUser || value; // Return found user or keep the partial object
        }
        return value;
    };

    const getOptionLabel = (option: any) => {
        if (!option) return "";
        if (typeof option === "string") return option;
        return option.name || option.id?.toString() || "";
    };

    const isOptionEqualToValue = (option: any, value: any) => {
        if (!option || !value) return false;
        if (typeof option === "object" && typeof value === "object") {
            return option.id === value.id;
        }
        return option === value;
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={null}
            render={({ field, fieldState: { error } }) => {
                // Resolve the value to a full user object if needed
                const resolvedValue = findUserById(field.value);

                return (
                    <div className="flex flex-col w-full">
                        <FormLabel>{label}</FormLabel>
                        <Autocomplete
                            {...field}
                            options={users}
                            getOptionLabel={getOptionLabel}
                            isOptionEqualToValue={isOptionEqualToValue}
                            value={resolvedValue || null}
                            onChange={(event, newValue) => {
                                field.onChange(newValue);
                            }}
                            onInputChange={(event, newInputValue) => {
                                setInputValue(newInputValue);
                            }}
                            inputValue={inputValue}
                            className={classes.root}
                            fullWidth
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            renderInput={(params) => (
                                <>
                                    <TextField
                                        {...params}
                                        placeholder={placeholder}
                                        error={!!errors[name]}
                                    />
                                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                                </>
                            )}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props;
                                return (
                                    <Box
                                        component="li"
                                        key={option.id || key}
                                        {...optionProps}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                            py: 1.5,
                                            px: 2,
                                            "&:hover": {
                                                backgroundColor: "#F3F4F6",
                                            },
                                        }}
                                    >
                                        <Avatar
                                            sx={{ width: 48, height: 48, flexShrink: 0 }}
                                            alt={option.name}
                                        >
                                            {option?.profileimage ? (
                                                <ThumbnailImage
                                                    s3Key={option.profileimage}
                                                    bucket="urn-assets"
                                                    width={48}
                                                    height={48}
                                                />
                                            ) : (
                                                option.name?.charAt(0)?.toUpperCase() || "?"
                                            )}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.875rem",
                                                    color: "#1F2937",
                                                    mb: 0.5,
                                                }}
                                            >
                                                {option.name || "Unknown"}
                                            </Box>
                                            <Box
                                                sx={{
                                                    fontSize: "0.75rem",
                                                    color: "#6B7280",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 0.25,
                                                }}
                                            >
                                                <span>Phone: {option.phonenum || "N/A"}</span>
                                                <span>
                                                    ID: {option.id} | Role: {option.role || "N/A"}
                                                </span>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            }}
                        />
                    </div>
                );
            }}
        />
    );
};

